import React, { useState, useMemo } from 'react';
import { Calculator, ShieldCheck, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { calculateHDFCPolicyPremium } from '../lib/policiesData';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEOHead';

// Static conversion rates (assuming base INR)
const RATES = {
  INR: { symbol: '₹', multiplier: 1, label: 'INR' },
  USD: { symbol: '$', multiplier: 1 / 83, label: 'USD' },
  EUR: { symbol: '€', multiplier: 1 / 89, label: 'EUR' }
};

const NRICalculator = () => {
  const { t } = useTranslation();
  const [currency, setCurrency] = useState('USD');
  const [baseCover, setBaseCover] = useState('25L');
  const [deductible, setDeductible] = useState('0');
  const [members, setMembers] = useState([
    { id: '1', role: 'Father', age: 40 },
    { id: '2', role: 'Mother', age: 38 },
    { id: '3', role: 'Child', age: 23 }
  ]);

  // Derived values
  const curr = RATES[currency];
  
  // Format currency helper
  const formatMoney = (amountInINR) => {
    const converted = amountInINR * curr.multiplier;
    return `${curr.symbol}${converted.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  };

  const addMember = () => {
    setMembers([
      ...members, 
      { id: Date.now().toString(), role: 'Brother/Sister', age: 30 }
    ]);
  };

  const removeMember = (id) => {
    if (members.length <= 1) return; // Need at least 1 member
    setMembers(members.filter(m => m.id !== id));
  };

  const updateMember = (id, field, value) => {
    setMembers(members.map(m => m.id === id ? { ...m, [field]: value } : m));
  };

  // Total calculated premium per year in INR based on exact ages and plan selection
  const annualPremiumINR = useMemo(() => calculateHDFCPolicyPremium(members, baseCover, deductible), [members, baseCover, deductible]);
  
  // Deriving Coverage Text limits based on Optima Secure Multiplier Rules
  const day1CoverINR = baseCover === '25L' ? 7500000 : 3000000;
  const year2CoverINR = baseCover === '25L' ? 10000000 : 4000000;

  const handleWhatsAppQuote = (e) => {
    e.preventDefault();
    let message = `Hi VSR Insurance Plus,\nI am interested in an HDFC Health policy.\n\nPlan Variables:\n- Base Cover: ${baseCover}\n- Deductible: ${deductible === '0' ? 'Zero' : deductible}\n\nHere are my exact family details:\n`;
    members.forEach((m) => {
      message += `- ${m.role} : ${m.age} years old\n`;
    });
    message += `\nPlease provide a custom quote for this specific matrix.`;
    
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/919849664800?text=${encodedMessage}`, '_blank');
  };

  return (
    <div className="min-h-screen pt-28 pb-20 bg-slate-50 dark:bg-slate-950">
      <SEOHead
        title="NRI Health Insurance Calculator | Indicative Family Floater Premiums"
        description="Calculate family health insurance and floater coverage premiums in USD, EUR, or INR for parents and families in India. Customized calculations by V. S. Reddy."
        canonicalPath="/nri-calculator"
        breadcrumbs={[{ name: 'NRI Calculator', path: '/nri-calculator' }]}
      />
      <div className="container mx-auto px-6 max-w-6xl">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center justify-center p-3 bg-orange-100 dark:bg-orange-900/30 rounded-full mb-6 relative">
            <Calculator className="w-8 h-8 text-orange-600 dark:text-orange-500" />
            <div className="absolute -top-2 -right-2 bg-rose-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md animate-bounce">NEW</div>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white mb-6">
            {t('advanced_nri_simulator')}
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            {t('nri_simulator_desc')}
          </p>
          
          {/* Currency Toggle */}
          <div className="mt-8 inline-flex bg-slate-200 dark:bg-slate-800 p-1 rounded-xl shadow-inner border border-slate-300 dark:border-slate-700">
            {Object.keys(RATES).map(c => (
              <button
                key={c}
                onClick={() => setCurrency(c)}
                className={`py-2 px-6 rounded-lg text-sm font-bold transition-all ${currency === c ? 'bg-white dark:bg-slate-950 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-10">
          
          {/* Detailed Controls */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            {/* Family Builder */}
            <div className="bg-white dark:bg-slate-900 p-8 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">{t('family_matrix')}</h3>
                <span className="text-xs font-bold px-2.5 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded-full">
                  {members.length} {members.length === 1 ? t('member') : t('members')}
                </span>
              </div>
              
              <div className="space-y-4 mb-6 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                {members.map((m, index) => (
                  <div key={m.id} className="p-4 bg-slate-50 dark:bg-slate-950 rounded-2xl flex items-center justify-between border border-slate-100 dark:border-slate-800 hover:border-blue-200 transition-colors group">
                    <div className="flex gap-4 items-center">
                      <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 flex items-center justify-center font-bold text-sm">
                        {index + 1}
                      </div>
                      <div className="flex flex-col gap-1 w-32">
                        <select 
                          className="bg-transparent font-semibold text-slate-900 dark:text-white text-sm focus:outline-none"
                          value={m.role}
                          onChange={(e) => updateMember(m.id, 'role', e.target.value)}
                        >
                          <option value="Father">{t('role_father')}</option>
                          <option value="Mother">{t('role_mother')}</option>
                          <option value="Brother">{t('role_brother')}</option>
                          <option value="Sister">{t('role_sister')}</option>
                          <option value="Spouse">{t('role_spouse')}</option>
                          <option value="Child">{t('role_child')}</option>
                          <option value="Father-in-law">{t('role_father_in_law')}</option>
                          <option value="Mother-in-law">{t('role_mother_in_law')}</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="flex flex-col">
                        <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">{t('age')}</span>
                        <input 
                          type="number"
                          value={m.age}
                          min="0" max="100"
                          onChange={(e) => updateMember(m.id, 'age', Number(e.target.value))}
                          className="w-16 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md py-1 px-2 text-sm font-bold text-slate-900 dark:text-white text-center focus:outline-none focus:border-primary"
                        />
                      </div>
                      <button 
                        onClick={() => removeMember(m.id)}
                        className={`p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-colors ${members.length <= 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={members.length <= 1}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              <button 
                onClick={addMember}
                className="w-full py-3 border-2 border-dashed border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-400 font-semibold rounded-2xl hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" /> {t('add_family_member')}
              </button>
            </div>

            {/* Plan Configuration */}
            <div className="bg-white dark:bg-slate-900 p-8 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">{t('plan_parameters')}</h3>
              
              <div className="space-y-6">
                <div>
                  <label className="text-sm font-bold text-slate-700 dark:text-slate-300 block mb-3">{t('base_coverage')}</label>
                  <div className="flex gap-4">
                    {['10L', '25L'].map((cov) => (
                      <button
                        key={cov}
                        onClick={() => setBaseCover(cov)}
                        className={`flex-1 py-3 px-4 rounded-xl border text-sm font-bold transition-all ${baseCover === cov ? 'border-primary bg-primary/10 text-primary' : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-300'}`}
                      >
                        {cov === '10L' ? t('10_lakhs') : t('25_lakhs')}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-bold text-slate-700 dark:text-slate-300 block mb-3">{t('deductible')}</label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { val: '0', label: t('deductible_zero') },
                      { val: '25K', label: t('deductible_25k') },
                      { val: '50K', label: t('deductible_50k') }
                    ].map((ded) => (
                      <button
                        key={ded.val}
                        onClick={() => setDeductible(ded.val)}
                        className={`py-2 px-1 rounded-xl border text-xs sm:text-sm font-bold transition-all ${deductible === ded.val ? 'border-primary bg-primary/10 text-primary' : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-300'}`}
                      >
                        {ded.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Results Analysis Panel */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <div className="bg-slate-900 dark:bg-slate-800 text-white p-8 md:p-10 rounded-3xl shadow-2xl relative overflow-hidden flex-grow flex flex-col">
              <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gradient-to-bl from-primary/30 to-emerald-500/10 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3 pointer-events-none" />
              
              {/* Focused Day 1 Highlighting */}
              <div className="relative z-10 flex-grow flex flex-col justify-center">
                <div className="inline-flex gap-2 items-center px-4 py-2 bg-gradient-to-r from-emerald-500/20 to-teal-500/10 border border-emerald-500/30 rounded-xl mb-8 w-max">
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                  <span className="text-emerald-300 font-bold tracking-wide uppercase text-xs">{t('immediate_assurance')}</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-extrabold leading-relaxed mb-6">
                  {t('from_just')} <span className="text-emerald-400">{formatMoney(annualPremiumINR)}</span> {t('per_year_family_of')} {members.length} {t('is_fully_covered_for')} <span className="text-white bg-blue-600 px-3 py-1 rounded-lg">{formatMoney(day1CoverINR)}</span> {t('from_day_1')}
                </h3>
                <p className="text-slate-400 font-medium text-base md:text-lg leading-relaxed">
                  {t('with_hdfc_multiplier')} <span className="text-emerald-400 font-bold">{formatMoney(year2CoverINR)}</span> {t('automatically_after_2nd_year')}
                </p>
              </div>
            </div>

            {/* Quick CTAs */}
            <div className="grid grid-cols-2 gap-6 mt-2">
               <Link to="/insurance-plans" className="p-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl group transition-all hover:border-primary shadow-sm text-center">
                 <h4 className="font-bold text-slate-900 dark:text-white group-hover:text-primary transition-colors">{t('see_hdfc_plans')}</h4>
               </Link>
               <button onClick={handleWhatsAppQuote} className="p-5 bg-primary rounded-2xl flex items-center justify-center gap-3 group hover:bg-blue-600 transition-all shadow-md shadow-primary/20 text-center w-full">
                 <h4 className="font-bold text-white">{t('get_custom_quote')}</h4>
                 <ChevronRight className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform" />
               </button>
            </div>
          </div>

        </div>

        <div className="mt-8 text-center text-xs text-slate-400 max-w-2xl mx-auto leading-relaxed">
          {t('nri_calc_note')}
        </div>

      </div>
    </div>
  );
};

export default NRICalculator;
